
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastmessageService } from 'src/app/toastmessage.service';
import { ConfirmationMessageDialogComponent } from '../confirmation-message-dialog/confirmation-message-dialog.component';
import { MessMenuScenarioApi, MessService } from '../mess.service';

export interface MenuDialogItem {
  id: string;
  menuItemId: number;
  itemName: string;
  daysheetMenuItemName: string;
  teluguItemName: string;
  itemFusionId?: string;
}

export interface DaysheetMenuItemPayload {
  daysheetMenuItemName: string;
  daysheetMenuItemId: number;
  status?: string;
  itemStatus?: string;
}

export interface MenuScenarioTab {
  scenarioId: number;
  scenarioName: string;
  icon: string;
}

export interface ScenarioBasedMenuItemPayload {
  scenarioId: number;
  scenarioName: string;
  daysheetMenuItemsList: DaysheetMenuItemPayload[];
  saveInventoryBalanceDetails?: any[];
}

export interface MenuAllowedMeals {
  breakfast: boolean;
  lunch: boolean;
  snacks: boolean;
  dinner: boolean;
}

export interface MenuDialogSubmitResult {
  scenarioBasedMenuItemsList: ScenarioBasedMenuItemPayload[];
  totalSelected: number;
  menuSubmitted: boolean;
  allowedMealScenarios: MenuAllowedMeals;
  allowedScenarioIds: number[];
}

@Component({
  selector: 'app-admin-menu-dialog',
  templateUrl: './admin-menu-dialog.component.html',
  styleUrls: ['./admin-menu-dialog.component.scss']
})
export class AdminMenuDialogComponent implements OnInit {

  scenarioTabs: MenuScenarioTab[] = [];
  activeScenarioId: number | null = null;
  searchText = '';

  private itemsByScenario: Record<number, MenuDialogItem[]> = {};
  private selectedByScenario: Record<number, Set<string>> = {};
  /** Per-item status from saved menu data (`1` = active, `0` = removed). */
  private itemStatusById: Record<string, string> = {};
  /** Items originally loaded from saved daysheet menu data. */
  private savedItemIds = new Set<string>();
  private daysheetId: number | null = null;
  /**
   * First-time / empty InventoryDetails — add menu locally after Submit confirmation (no API).
   * Cleared when loadInitialData receives a valid response with status PENDING.
   */
  private inventoryDetailsLoadedEmpty = false;

  constructor(
    private dialogRef: MatDialogRef<AdminMenuDialogComponent>,
    private matDialog: MatDialog,
    private messService: MessService,
    private spinner: NgxSpinnerService,
    private toast: ToastmessageService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      buildingId?: number | string;
      inventoryDate?: string;
      daysheetId?: number | string;
      scenarioBasedMenuItemsList?: ScenarioBasedMenuItemPayload[];
      daysheetStatus?: string;
      /** When true, block adding new menu items (holiday daysheet from messReport). */
      isHoliday?: boolean;
      /** When true, restore menu selections passed from parent (same date, submitted this session). */
      restoreLocalMenu?: boolean;
      onInventoryUpdated?: () => void;
    },
  ) { console.log('menu dialog data', this.data); }

  ngOnInit(): void {
    const parsedDaysheetId = Number(this.data?.daysheetId);
    this.daysheetId = Number.isFinite(parsedDaysheetId) ? parsedDaysheetId : null;
    this.loadInitialData();
  }

  get activeScenarioName(): string {
    return this.scenarioTabs.find((tab) => tab.scenarioId === this.activeScenarioId)?.scenarioName ?? '';
  }

  get selectedItems(): MenuDialogItem[] {
    if (this.activeScenarioId == null) {
      return [];
    }
    const selected = this.selectedByScenario[this.activeScenarioId] ?? new Set<string>();
    return (this.itemsByScenario[this.activeScenarioId] ?? []).filter(
      (item) => selected.has(item.id) && this.isItemActive(item.id),
    );
  }

  get availableItems(): MenuDialogItem[] {
    if (this.activeScenarioId == null) {
      return [];
    }
    const selected = this.selectedByScenario[this.activeScenarioId] ?? new Set<string>();
    const query = this.searchText.trim().toLowerCase();
    return (this.itemsByScenario[this.activeScenarioId] ?? []).filter((item) => {
      if (selected.has(item.id)) {
        return false;
      }
      if (!query) {
        return true;
      }
      return (
        item.daysheetMenuItemName.toLowerCase().includes(query) ||
        item.itemName.toLowerCase().includes(query) ||
        item.teluguItemName.toLowerCase().includes(query)
      );
    });
  }

  get activeScenarioSelectedCount(): number {
    return this.selectedItems.length;
  }

  get totalSelectedCount(): number {
    return this.scenarioTabs.reduce((sum, tab) => {
      const selected = this.selectedByScenario[tab.scenarioId] ?? new Set<string>();
      const activeCount = (this.itemsByScenario[tab.scenarioId] ?? []).filter(
        (item) => selected.has(item.id) && this.isItemActive(item.id),
      ).length;
      return sum + activeCount;
    }, 0);
  }

  get isMenuReadOnly(): boolean {
    return false;
  }

  get isAddDisabled(): boolean {
    return this.isMenuReadOnly || this.data?.isHoliday === true;
  }

  get isPendingStatus(): boolean {
    return String(this.data?.daysheetStatus ?? '').trim().toUpperCase() === 'PENDING';
  }

  selectScenario(scenarioId: number): void {
    this.activeScenarioId = scenarioId;
    this.searchText = '';
  }

  addItem(item: MenuDialogItem): void {
    if (this.isAddDisabled || this.activeScenarioId == null) {
      return;
    }
    this.searchText = '';
    const itemName = String(item.daysheetMenuItemName || item.itemName || 'this meal item').trim();
    const localOnlyAdd = this.inventoryDetailsLoadedEmpty;
    const confirmRef = this.matDialog.open(ConfirmationMessageDialogComponent, {
      width: '400px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      data: {
        title: 'Add Menu Item',
        message: `Are you sure you want to add ${itemName}?`,
        confirmLabel: localOnlyAdd ? 'Submit' : 'Update',
        confirmResult: localOnlyAdd ? 'submit' : 'update',
      },
    });
    confirmRef.afterClosed().subscribe((result) => {
      if (localOnlyAdd && result === 'submit') {
        this.confirmAddItem(item);
        return;
      }
      if (!localOnlyAdd && result === 'update') {
        this.confirmAddItem(item);
      }
    });
  }

  private confirmAddItem(item: MenuDialogItem): void {
    if (this.isAddDisabled || this.activeScenarioId == null) {
      return;
    }
    const scenarioId = this.activeScenarioId;
    if (this.inventoryDetailsLoadedEmpty) {
      this.applyLocalMenuItemAdd(item, scenarioId);
      return;
    }
    const payload = this.buildMenuAddPayload(item);
    if (!payload) {
      this.toast.showError('Unable to add menu item.');
      return;
    }
    this.spinner.show();
    this.messService.adminSaveInventoryDetails(payload).subscribe({
      next: () => {
        this.applyLocalMenuItemAdd(item, scenarioId, true);
        this.reloadInventoryDetails();
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
        this.toast.showError('Failed to add menu item.');
      },
    });
  }

  /** Add item to selected menu list (menu-item row) and parent scenario payload. */
  private applyLocalMenuItemAdd(
    item: MenuDialogItem,
    scenarioId: number,
    markPersisted = false,
  ): void {
    this.itemStatusById[item.id] = '1';
    if (markPersisted) {
      this.savedItemIds.add(item.id);
    }
    this.addToSelected(scenarioId, item.id);
    this.ensureMenuItemInParentScenario(item, '1');
    this.data.restoreLocalMenu = true;
  }

  removeItem(item: MenuDialogItem): void {
    if (this.isMenuReadOnly || this.activeScenarioId == null) {
      return;
    }
    const itemName = String(item.daysheetMenuItemName || item.itemName || 'this meal item').trim();
    const confirmRef = this.matDialog.open(ConfirmationMessageDialogComponent, {
      width: '400px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      data: {
        title: 'Meal item request Delete',
        message: `Are you sure you want to delete ${itemName}?`,
      },
    });
    confirmRef.afterClosed().subscribe((result) => {
      if (result === 'delete') {
        this.confirmRemoveItem(item);
      }
    });
  }

  private confirmRemoveItem(item: MenuDialogItem): void {
    if (this.isMenuReadOnly || this.activeScenarioId == null) {
      return;
    }
    const scenarioId = this.activeScenarioId;
    if (this.savedItemIds.has(item.id) && this.isItemActive(item.id)) {
      this.saveMenuItemStatus(item, '0', () => {
        this.itemStatusById[item.id] = '0';
        this.removeFromSelected(scenarioId, item.id);
        this.updateLocalMenuItemStatus(item, '0');
      });
      return;
    }
    delete this.itemStatusById[item.id];
    this.savedItemIds.delete(item.id);
    this.removeFromSelected(scenarioId, item.id);
    this.updateLocalMenuItemStatus(item, '0');
  }

  private isItemActive(itemId: string): boolean {
    return String(this.itemStatusById[itemId] ?? '1') !== '0';
  }

  private addToSelected(scenarioId: number, itemId: string): void {
    const current = this.selectedByScenario[scenarioId] ?? new Set<string>();
    this.selectedByScenario[scenarioId] = new Set([...current, itemId]);
  }

  private removeFromSelected(scenarioId: number, itemId: string): void {
    const current = this.selectedByScenario[scenarioId] ?? new Set<string>();
    const next = new Set(current);
    next.delete(itemId);
    this.selectedByScenario[scenarioId] = next;
  }

  private updateLocalMenuItemStatus(item: MenuDialogItem, status: string): void {
    if (this.activeScenarioId == null) {
      return;
    }
    const scenarioId = this.activeScenarioId;
    const list = this.data.scenarioBasedMenuItemsList ?? [];
    const scenario = list.find((entry) => Number(entry.scenarioId) === scenarioId);
    if (!scenario) {
      return;
    }
    const normalizedName = item.daysheetMenuItemName.trim().toLowerCase();
    const match = (scenario.daysheetMenuItemsList ?? []).find((menuItem) => {
      const menuItemId = Number(
        menuItem.daysheetMenuItemId ??
        (menuItem as { DaysheetMenuItemId?: number }).DaysheetMenuItemId,
      );
      const menuItemName = String(
        menuItem.daysheetMenuItemName ??
        (menuItem as { DaysheetMenuItemName?: string }).DaysheetMenuItemName ??
        '',
      )
        .trim()
        .toLowerCase();
      return (
        (Number.isFinite(menuItemId) && menuItemId === item.menuItemId) ||
        (!!normalizedName && menuItemName === normalizedName)
      );
    });
    if (!match) {
      return;
    }
    match.status = status;
    match.itemStatus = status;
  }

  itemImageUrl(item: MenuDialogItem): string | null {
    if (!item.itemFusionId) {
      return null;
    }
    return `http://mess-images.s3.ap-south-1.amazonaws.com/medium/${item.itemFusionId}.jpg`;
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.isMenuReadOnly) {
      return;
    }

    if (!this.totalSelectedCount) {
      this.toast.showError('Please select at least one menu item before submit.');
      return;
    }

    if (this.isPendingStatus) {
      const duplicateName = this.findDuplicateSelectedItemName();
      if (duplicateName) {
        this.toast.showError(`Duplicate menu item "${duplicateName}" is not allowed. Please remove duplicate items before submit.`);
        return;
      }
    }

    const scenarioBasedMenuItemsList: ScenarioBasedMenuItemPayload[] = this.scenarioTabs
      .map((scenario) => {
        const selected = this.selectedByScenario[scenario.scenarioId] ?? new Set<string>();
        const activeItems = (this.itemsByScenario[scenario.scenarioId] ?? [])
          .filter((item) => selected.has(item.id) && this.isItemActive(item.id))
          .map((item) => this.toDaysheetMenuItemPayload(item));
        const removedItems = (this.itemsByScenario[scenario.scenarioId] ?? [])
          .filter(
            (item) =>
              this.savedItemIds.has(item.id) &&
              String(this.itemStatusById[item.id] ?? '1') === '0' &&
              !selected.has(item.id),
          )
          .map((item) => this.toDaysheetMenuItemPayload(item));
        const daysheetMenuItemsList = [...activeItems, ...removedItems];
        const previousScenario = (this.data?.scenarioBasedMenuItemsList ?? []).find(
          (s) => Number(s.scenarioId) === scenario.scenarioId,
        );
        return {
          scenarioId: scenario.scenarioId,
          scenarioName: scenario.scenarioName,
          daysheetMenuItemsList,
          saveInventoryBalanceDetails: previousScenario?.saveInventoryBalanceDetails ?? [],
        };
      })
      .filter((scenario) => scenario.daysheetMenuItemsList.length > 0);

    const result: MenuDialogSubmitResult = {
      scenarioBasedMenuItemsList,
      totalSelected: this.totalSelectedCount,
      menuSubmitted: true,
      allowedMealScenarios: this.buildAllowedMealScenariosFromList(scenarioBasedMenuItemsList),
      allowedScenarioIds: this.buildAllowedScenarioIdsFromList(scenarioBasedMenuItemsList),
    };

    console.log('menu-dialog submit — scenarioBasedMenuItemsList', scenarioBasedMenuItemsList);

    this.scenarioTabs.forEach((scenario) => {
      this.selectedByScenario[scenario.scenarioId] = new Set<string>();
    });

    this.dialogRef.close(result);
  }

  private buildAllowedMealScenariosFromList(
    list: ScenarioBasedMenuItemPayload[],
  ): MenuAllowedMeals {
    const flags: MenuAllowedMeals = {
      breakfast: false,
      lunch: false,
      snacks: false,
      dinner: false,
    };
    (list ?? []).forEach((scenario) => {
      const hasActive = (scenario.daysheetMenuItemsList ?? []).some(
        (item) => String(item.status ?? '1') !== '0',
      );
      if (!hasActive) {
        return;
      }
      const name = String(scenario.scenarioName ?? '').trim().toLowerCase();
      if (name.includes('breakfast')) {
        flags.breakfast = true;
      }
      if (name.includes('lunch')) {
        flags.lunch = true;
      }
      if (name.includes('snack')) {
        flags.snacks = true;
      }
      if (name.includes('dinner')) {
        flags.dinner = true;
      }
    });
    return flags;
  }

  private buildAllowedScenarioIdsFromList(list: ScenarioBasedMenuItemPayload[]): number[] {
    return (list ?? [])
      .filter((scenario) =>
        (scenario.daysheetMenuItemsList ?? []).some((item) => String(item.status ?? '1') !== '0'),
      )
      .map((scenario) => Number(scenario.scenarioId))
      .filter((id) => Number.isFinite(id));
  }

  private loadInitialData(): void {
    const buildingId = this.data?.buildingId;
    const inventoryDate = String(this.data?.inventoryDate ?? '').trim();
    const restoreLocalMenu = this.data?.restoreLocalMenu === true;
    const parentMenuSnapshot = restoreLocalMenu
      ? this.cloneScenarioMenuList(this.data?.scenarioBasedMenuItemsList ?? [])
      : [];
    if (!buildingId || !inventoryDate) {
      this.inventoryDetailsLoadedEmpty = true;
      this.data.scenarioBasedMenuItemsList = parentMenuSnapshot;
      this.loadMenuCard();
      return;
    }
    this.spinner.show();
    this.messService.adminMessInventory(buildingId, inventoryDate).subscribe({
      next: (res: any) => {
        this.applyInventoryResponse(res, parentMenuSnapshot, restoreLocalMenu);
        this.loadMenuCard(true);
      },
      error: () => {
        this.inventoryDetailsLoadedEmpty = true;
        this.data.scenarioBasedMenuItemsList = parentMenuSnapshot;
        this.loadMenuCard(true);
      },
    });
  }

  private isEmptyInventoryDetailsResponse(res: any): boolean {
    if (res == null || res === '' || res === 204) {
      return true;
    }
    if (Array.isArray(res)) {
      return res.length === 0;
    }
    if (typeof res !== 'object') {
      return true;
    }
    const hasStatus = String(res?.status ?? '').trim() !== '';
    const hasDaysheetId = Number.isFinite(Number(res?.daysheetId));
    return !hasStatus && !hasDaysheetId;
  }

  private cloneScenarioMenuList(list: ScenarioBasedMenuItemPayload[]): ScenarioBasedMenuItemPayload[] {
    return (list ?? []).map((scenario) => ({
      scenarioId: Number(scenario.scenarioId),
      scenarioName: String(scenario.scenarioName ?? '').trim(),
      daysheetMenuItemsList: (scenario.daysheetMenuItemsList ?? []).map((item) => ({
        daysheetMenuItemId: Number(item.daysheetMenuItemId),
        daysheetMenuItemName: String(item.daysheetMenuItemName ?? '').trim(),
        status: String(item.status ?? item.itemStatus ?? '1'),
        itemStatus: String(item.itemStatus ?? item.status ?? '1'),
      })),
      saveInventoryBalanceDetails: Array.isArray(scenario.saveInventoryBalanceDetails)
        ? [...scenario.saveInventoryBalanceDetails]
        : [],
    }));
  }

  private hasActiveMenuInList(list: ScenarioBasedMenuItemPayload[]): boolean {
    return (list ?? []).some((scenario) =>
      (scenario.daysheetMenuItemsList ?? []).some((item) => String(item.status ?? '1') !== '0'),
    );
  }

  private mergeDaysheetMenuItemsById(
    existing: DaysheetMenuItemPayload[],
    incoming: DaysheetMenuItemPayload[],
  ): DaysheetMenuItemPayload[] {
    const byId = new Map<number, DaysheetMenuItemPayload>();
    (existing ?? []).forEach((item) => {
      const id = Number(item.daysheetMenuItemId);
      if (Number.isFinite(id)) {
        byId.set(id, item);
      }
    });
    (incoming ?? []).forEach((item) => {
      const id = Number(item.daysheetMenuItemId);
      if (!Number.isFinite(id)) {
        return;
      }
      const prev = byId.get(id);
      byId.set(id, prev ? { ...prev, ...item } : item);
    });
    return Array.from(byId.values());
  }

  private mergeScenarioMenuListsForDialog(
    parent: ScenarioBasedMenuItemPayload[],
    fromApi: ScenarioBasedMenuItemPayload[],
  ): ScenarioBasedMenuItemPayload[] {
    const parentById = new Map<number, ScenarioBasedMenuItemPayload>();
    (parent ?? []).forEach((scenario) => {
      const id = Number(scenario.scenarioId);
      if (Number.isFinite(id)) {
        parentById.set(id, scenario);
      }
    });
    const apiById = new Map<number, ScenarioBasedMenuItemPayload>();
    (fromApi ?? []).forEach((scenario) => {
      const id = Number(scenario.scenarioId);
      if (Number.isFinite(id)) {
        apiById.set(id, scenario);
      }
    });
    const allIds = new Set([...parentById.keys(), ...apiById.keys()]);
    return Array.from(allIds)
      .sort((a, b) => a - b)
      .map((scenarioId) => {
        const parentScenario = parentById.get(scenarioId);
        const apiScenario = apiById.get(scenarioId);
        if (!parentScenario) {
          return apiScenario!;
        }
        if (!apiScenario) {
          return parentScenario;
        }
        const parentMenu = parentScenario.daysheetMenuItemsList ?? [];
        const apiMenu = apiScenario.daysheetMenuItemsList ?? [];
        const parentHasActiveMenu = parentMenu.some((item) => String(item.status ?? '1') !== '0');
        const apiHasActiveMenu = apiMenu.some((item) => String(item.status ?? '1') !== '0');
        return {
          ...apiScenario,
          ...parentScenario,
          scenarioId: parentScenario.scenarioId,
          scenarioName: parentScenario.scenarioName || apiScenario.scenarioName,
          daysheetMenuItemsList: parentHasActiveMenu
            ? this.mergeDaysheetMenuItemsById(apiMenu, parentMenu)
            : apiHasActiveMenu
              ? apiMenu
              : this.mergeDaysheetMenuItemsById(apiMenu, parentMenu),
          saveInventoryBalanceDetails:
            apiScenario.saveInventoryBalanceDetails?.length
              ? apiScenario.saveInventoryBalanceDetails
              : parentScenario.saveInventoryBalanceDetails ?? [],
        };
      });
  }

  private applyInventoryResponse(
    res: any,
    parentMenuSnapshot: ScenarioBasedMenuItemPayload[] = [],
    restoreLocalMenu = false,
  ): void {
    if (this.isEmptyInventoryDetailsResponse(res)) {
      this.inventoryDetailsLoadedEmpty = true;
      this.data.scenarioBasedMenuItemsList = restoreLocalMenu ? parentMenuSnapshot : [];
      return;
    }
    this.inventoryDetailsLoadedEmpty = false;
    const apiStatus = String(res?.status ?? '').trim();
    if (apiStatus) {
      this.data.daysheetStatus = apiStatus;
    }
    const nextDaysheetId = Number(res.daysheetId);
    if (Number.isFinite(nextDaysheetId)) {
      this.daysheetId = nextDaysheetId;
    }
    const fromApi = this.extractScenarioListFromInventory(res);
    const parent = restoreLocalMenu
      ? (parentMenuSnapshot.length
        ? parentMenuSnapshot
        : this.cloneScenarioMenuList(this.data?.scenarioBasedMenuItemsList ?? []))
      : [];
    const apiHasActiveMenu = this.hasActiveMenuInList(fromApi);
    const parentHasActiveMenu = this.hasActiveMenuInList(parent);

    if (apiHasActiveMenu && restoreLocalMenu && parentHasActiveMenu) {
      this.data.scenarioBasedMenuItemsList = this.mergeScenarioMenuListsForDialog(parent, fromApi);
    } else if (apiHasActiveMenu) {
      this.data.scenarioBasedMenuItemsList = fromApi;
    } else if (restoreLocalMenu && (parentHasActiveMenu || parent.length)) {
      this.data.scenarioBasedMenuItemsList = parent;
    } else if (fromApi.length) {
      this.data.scenarioBasedMenuItemsList = fromApi;
    } else {
      this.data.scenarioBasedMenuItemsList = [];
    }
  }

  private extractScenarioListFromInventory(res: any): ScenarioBasedMenuItemPayload[] {
    const raw = res?.ScenarioBasedMenuItemsList ?? res?.scenarioBasedMenuItemsList ?? [];
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((scenario: any) => ({
        scenarioId: Number(scenario?.scenarioId),
        scenarioName: String(scenario?.scenarioName ?? '').trim(),
        daysheetMenuItemsList: (Array.isArray(scenario?.daysheetMenuItemsList) ? scenario.daysheetMenuItemsList : [])
          .map((item: any) => ({
            daysheetMenuItemId: Number(item?.daysheetMenuItemId ?? item?.DaysheetMenuItemId),
            daysheetMenuItemName: String(item?.daysheetMenuItemName ?? item?.DaysheetMenuItemName ?? '').trim(),
            status: String(item?.status ?? '1').trim() || '1',
          }))
          .filter((item: DaysheetMenuItemPayload) => Number.isFinite(item.daysheetMenuItemId) && !!item.daysheetMenuItemName),
        saveInventoryBalanceDetails:
          scenario?.saveInventoryBalanceDetails ?? scenario?.SaveInventoryBalanceDetails ?? [],
      }))
      .filter((scenario) => Number.isFinite(scenario.scenarioId) && !!scenario.scenarioName);
  }

  private reloadInventoryDetails(): void {
    const buildingId = this.data?.buildingId;
    const inventoryDate = String(this.data?.inventoryDate ?? '').trim();
    if (!buildingId || !inventoryDate) {
      return;
    }
    const parentMenuSnapshot = this.cloneScenarioMenuList(this.data?.scenarioBasedMenuItemsList ?? []);
    const restoreLocalMenu =
      this.data?.restoreLocalMenu === true || this.hasActiveMenuInList(parentMenuSnapshot);
    this.messService.adminMessInventory(buildingId, inventoryDate).subscribe({
      next: (res: any) => {
        this.applyInventoryResponse(res, parentMenuSnapshot, restoreLocalMenu);
        this.resetItemStatusTracking();
        this.scenarioTabs.forEach((scenario) => {
          this.selectedByScenario[scenario.scenarioId] = new Set<string>();
        });
        this.restorePreviousSelections();
        this.data.onInventoryUpdated?.();
      },
    });
  }

  private saveMenuItemStatus(item: MenuDialogItem, status: string, onSuccess?: () => void): void {
    if (this.activeScenarioId == null) {
      return;
    }
    const scenario = this.scenarioTabs.find((tab) => tab.scenarioId === this.activeScenarioId);
    const payload = {
      itemType: 'Menu',
      itemId: item.menuItemId,
      status,
      scenarioId: this.activeScenarioId,
      daysheetId: this.daysheetId,
      menuType: scenario?.scenarioName ?? '',
      bfQty: 0,
      lunchQty: 0,
      snacksQty: 0,
      dinnerQty: 0,
      onHandQty: 0,
      inventoryDate: String(this.data?.inventoryDate ?? '').trim(),
      ratePerUnit: 0,
      buildingId: this.data?.buildingId,
    };
    this.spinner.show();
    this.messService.adminSaveInventoryDetails(payload).subscribe({
      next: () => {
        onSuccess?.();
        this.reloadInventoryDetails();
        this.toast.showSuccess('Successfully to remove menu item.');
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
        this.toast.showError('Failed to update menu item.');
      },
    });
  }

  private buildMenuAddPayload(item: MenuDialogItem): Record<string, unknown> | null {
    if (this.activeScenarioId == null || !Number.isFinite(item.menuItemId)) {
      return null;
    }
    const scenario = this.scenarioTabs.find((tab) => tab.scenarioId === this.activeScenarioId);

    return {
      itemType: 'Menu',
      itemId: item.menuItemId,
      status: '1',
      scenarioId: this.activeScenarioId,
      daysheetId: this.daysheetId,
      menuType: scenario?.scenarioName ?? '',
      bfQty: 0,
      lunchQty: 0,
      snacksQty: 0,
      dinnerQty: 0,
      onHandQty: 0,
      inventoryDate: String(this.data?.inventoryDate ?? '').trim(),
      ratePerUnit: 0,
      buildingId: this.data?.buildingId,
    };
  }

  private ensureMenuItemInParentScenario(item: MenuDialogItem, status: string): void {
    if (this.activeScenarioId == null) {
      return;
    }
    const list = this.data.scenarioBasedMenuItemsList ?? [];
    if (!this.data.scenarioBasedMenuItemsList) {
      this.data.scenarioBasedMenuItemsList = list;
    }
    let scenario = list.find((entry) => Number(entry.scenarioId) === this.activeScenarioId);
    if (!scenario) {
      const tab = this.scenarioTabs.find((entry) => entry.scenarioId === this.activeScenarioId);
      scenario = {
        scenarioId: this.activeScenarioId,
        scenarioName: tab?.scenarioName ?? '',
        daysheetMenuItemsList: [],
        saveInventoryBalanceDetails: [],
      };
      list.push(scenario);
    }
    const menuPayload = this.toDaysheetMenuItemPayload(item);
    const menuList = scenario.daysheetMenuItemsList ?? [];
    const existing = menuList.find(
      (menuItem) => Number(menuItem.daysheetMenuItemId) === item.menuItemId,
    );
    if (existing) {
      existing.status = status;
      existing.itemStatus = status;
    } else {
      menuList.push({
        ...menuPayload,
        status,
        itemStatus: status,
      });
    }
    scenario.daysheetMenuItemsList = menuList;
  }

  private loadMenuCard(skipSpinnerShow = false): void {
    if (!skipSpinnerShow) {
      this.spinner.show();
    }
    this.messService.getMenuCard().subscribe({
      next: (res) => {
        const scenarios = Array.isArray(res) ? res : [];
        if (scenarios.length) {
          this.applyApiScenarios(this.alignApiScenariosWithPreviousData(scenarios));
        } else if (this.data?.scenarioBasedMenuItemsList?.length) {
          this.scenarioTabs = [];
          this.itemsByScenario = {};
          this.selectedByScenario = {};
          this.resetItemStatusTracking();
          this.mergeScenariosFromPreviousData();
        } else {
          this.applyDefaultMenu();
        }
        this.mergeScenariosFromPreviousData();
        this.restorePreviousSelections();
        this.spinner.hide();
      },
      error: () => {
        if (this.data?.scenarioBasedMenuItemsList?.length) {
          this.scenarioTabs = [];
          this.itemsByScenario = {};
          this.selectedByScenario = {};
          this.resetItemStatusTracking();
          this.mergeScenariosFromPreviousData();
        } else {
          this.applyDefaultMenu();
          this.mergeScenariosFromPreviousData();
        }
        this.restorePreviousSelections();
        this.spinner.hide();
      },
    });
  }

  private applyApiScenarios(scenarios: MessMenuScenarioApi[]): void {
    this.scenarioTabs = scenarios.map((scenario) => ({
      scenarioId: Number(scenario.scenarioId),
      scenarioName: String(scenario.scenarioName ?? '').trim(),
      icon: this.getScenarioIcon(String(scenario.scenarioName ?? '')),
    }));

    this.itemsByScenario = {};
    this.selectedByScenario = {};
    this.resetItemStatusTracking();

    scenarios.forEach((scenario) => {
      const scenarioId = Number(scenario.scenarioId);
      const items = (scenario.messMenuItems ?? []).map((item, index) =>
        this.normalizeItem(
          {
            id: `scenario-${scenarioId}-${item.menuItemId ?? index}`,
            menuItemId: Number(item.menuItemId),
            itemName: String(item.itemNameEn ?? '').trim(),
            daysheetMenuItemName: String(item.itemNameEn ?? '').trim(),
            teluguItemName: String(item.itemNameTe ?? '').trim(),
          },
          scenarioId,
          index,
        ),
      );
      this.itemsByScenario[scenarioId] = items;
      this.selectedByScenario[scenarioId] = new Set<string>();
    });

    this.activeScenarioId = this.scenarioTabs[0]?.scenarioId ?? null;
  }

  private alignApiScenariosWithPreviousData(scenarios: MessMenuScenarioApi[]): MessMenuScenarioApi[] {
    const previous = this.data?.scenarioBasedMenuItemsList ?? [];
    if (!previous.length) {
      return scenarios;
    }

    const previousIdByName = new Map<string, number>();
    previous.forEach((scenario) => {
      const scenarioName = String(scenario.scenarioName ?? '').trim().toLowerCase();
      const scenarioId = Number(scenario.scenarioId);
      if (scenarioName && Number.isFinite(scenarioId)) {
        previousIdByName.set(scenarioName, scenarioId);
      }
    });

    return scenarios.map((scenario) => {
      const scenarioName = String(scenario.scenarioName ?? '').trim();
      const previousId = previousIdByName.get(scenarioName.toLowerCase());
      if (previousId == null) {
        return scenario;
      }
      return {
        ...scenario,
        scenarioId: previousId,
      };
    });
  }

  private mergeScenariosFromPreviousData(): void {
    const previous = this.data?.scenarioBasedMenuItemsList ?? [];
    if (!previous.length) {
      return;
    }

    previous.forEach((scenario) => {
      const scenarioId = Number(scenario.scenarioId);
      const scenarioName = String(scenario.scenarioName ?? '').trim();
      if (!Number.isFinite(scenarioId) || !scenarioName) {
        return;
      }

      const existingTab = this.scenarioTabs.find((tab) => tab.scenarioId === scenarioId);
      if (!existingTab) {
        this.scenarioTabs.push({
          scenarioId,
          scenarioName,
          icon: this.getScenarioIcon(scenarioName),
        });
      }

      if (!this.itemsByScenario[scenarioId]) {
        this.itemsByScenario[scenarioId] = [];
      }
      if (!this.selectedByScenario[scenarioId]) {
        this.selectedByScenario[scenarioId] = new Set<string>();
      }
    });

    this.scenarioTabs.sort((a, b) => a.scenarioId - b.scenarioId);
    if (this.activeScenarioId == null && this.scenarioTabs.length) {
      this.activeScenarioId = this.scenarioTabs[0].scenarioId;
    }
  }

  private restorePreviousSelections(): void {
    const previous = this.data?.scenarioBasedMenuItemsList ?? [];
    if (!previous.length) {
      return;
    }

    previous.forEach((scenario) => {
      const scenarioId = Number(scenario.scenarioId);
      const scenarioName = String(scenario.scenarioName ?? '').trim();
      if (!Number.isFinite(scenarioId)) {
        return;
      }

      if (!this.itemsByScenario[scenarioId]) {
        this.itemsByScenario[scenarioId] = [];
      }
      if (!this.selectedByScenario[scenarioId]) {
        this.selectedByScenario[scenarioId] = new Set<string>();
      }

      (scenario.daysheetMenuItemsList ?? []).forEach((selectedItem) => {
        const menuItemId = Number(
          selectedItem.daysheetMenuItemId ?? (selectedItem as { DaysheetMenuItemId?: number }).DaysheetMenuItemId,
        );
        const daysheetMenuItemName = String(
          selectedItem.daysheetMenuItemName ??
          (selectedItem as { DaysheetMenuItemName?: string }).DaysheetMenuItemName ??
          '',
        ).trim();
        const status = String(selectedItem.status ?? '1').trim() || '1';
        if (!Number.isFinite(menuItemId) || !daysheetMenuItemName) {
          return;
        }

        const normalizedName = daysheetMenuItemName.toLowerCase();
        let match =
          (this.itemsByScenario[scenarioId] ?? []).find((item) => item.menuItemId === menuItemId) ??
          (this.itemsByScenario[scenarioId] ?? []).find(
            (item) => (item.daysheetMenuItemName || item.itemName).trim().toLowerCase() === normalizedName,
          );

        if (!match) {
          match = this.normalizeItem(
            {
              id: `scenario-${scenarioId}-saved-${menuItemId}`,
              menuItemId,
              itemName: daysheetMenuItemName,
              daysheetMenuItemName,
              teluguItemName: '',
            },
            scenarioId,
            this.itemsByScenario[scenarioId].length,
          );
          this.itemsByScenario[scenarioId].push(match);
        } else {
          match.daysheetMenuItemName = daysheetMenuItemName;
          match.itemName = match.itemName || daysheetMenuItemName;
        }

        this.itemStatusById[match.id] = status;
        this.savedItemIds.add(match.id);
        if (status !== '0') {
          this.addToSelected(scenarioId, match.id);
        }
      });

      if (scenarioName && !this.scenarioTabs.some((tab) => tab.scenarioId === scenarioId)) {
        this.scenarioTabs.push({
          scenarioId,
          scenarioName,
          icon: this.getScenarioIcon(scenarioName),
        });
      }
    });

    this.scenarioTabs.sort((a, b) => a.scenarioId - b.scenarioId);
    if (this.activeScenarioId == null && this.scenarioTabs.length) {
      this.activeScenarioId = this.scenarioTabs[0].scenarioId;
    }
  }

  private findDuplicateSelectedItemName(): string | null {
    for (const scenario of this.scenarioTabs) {
      const selected = this.selectedByScenario[scenario.scenarioId] ?? new Set<string>();
      const seen = new Set<string>();
      for (const item of this.itemsByScenario[scenario.scenarioId] ?? []) {
        if (!selected.has(item.id) || !this.isItemActive(item.id)) {
          continue;
        }
        const nameKey = (item.daysheetMenuItemName || item.itemName).trim().toLowerCase();
        if (!nameKey) {
          continue;
        }
        if (seen.has(nameKey)) {
          return (item.daysheetMenuItemName || item.itemName).trim();
        }
        seen.add(nameKey);
      }
    }
    return null;
  }

  private applyDefaultMenu(): void {
    const defaults: {
      scenarioId: number;
      scenarioName: string;
      items: Omit<MenuDialogItem, 'menuItemId'>[];
    }[] = [
        {
          scenarioId: 1,
          scenarioName: 'Breakfast',
          items: [
            { id: 'bf-1', itemName: 'Idly', daysheetMenuItemName: 'Idly', teluguItemName: 'ఇడ్లీ' },
            { id: 'bf-2', itemName: 'Poori', daysheetMenuItemName: 'Poori', teluguItemName: 'పూరి' },
            { id: 'bf-3', itemName: 'Poha', daysheetMenuItemName: 'Poha', teluguItemName: 'పొహా' },
            { id: 'bf-4', itemName: 'Tea', daysheetMenuItemName: 'Tea', teluguItemName: 'టీ' },
          ],
        },
        {
          scenarioId: 2,
          scenarioName: 'Lunch',
          items: [
            { id: 'ln-1', itemName: 'Rice', daysheetMenuItemName: 'Rice', teluguItemName: 'అన్నం' },
            { id: 'ln-2', itemName: 'Dal', daysheetMenuItemName: 'Dal', teluguItemName: 'పప్పు' },
            { id: 'ln-3', itemName: 'Vegetable curry', daysheetMenuItemName: 'Vegetable curry', teluguItemName: 'కూర' },
          ],
        },
        {
          scenarioId: 3,
          scenarioName: 'Snacks',
          items: [
            { id: 'sn-1', itemName: 'Fruit salad', daysheetMenuItemName: 'Fruit salad', teluguItemName: 'ఫలాల సలాడ్' },
            { id: 'sn-2', itemName: 'Biscuits', daysheetMenuItemName: 'Biscuits', teluguItemName: 'బిస్కెట్లు' },
          ],
        },
        {
          scenarioId: 4,
          scenarioName: 'Dinner',
          items: [
            { id: 'dn-1', itemName: 'Idly', daysheetMenuItemName: 'Idly', teluguItemName: 'ఇడ్లీ' },
            { id: 'dn-2', itemName: 'Poori', daysheetMenuItemName: 'Poori', teluguItemName: 'పూరి' },
            { id: 'dn-3', itemName: 'Tea', daysheetMenuItemName: 'Tea', teluguItemName: 'టీ' },
          ],
        },
      ];

    this.scenarioTabs = defaults.map((scenario) => ({
      scenarioId: scenario.scenarioId,
      scenarioName: scenario.scenarioName,
      icon: this.getScenarioIcon(scenario.scenarioName),
    }));
    this.itemsByScenario = {};
    this.selectedByScenario = {};
    this.resetItemStatusTracking();

    defaults.forEach((scenario) => {
      this.itemsByScenario[scenario.scenarioId] = scenario.items.map((item, index) =>
        this.normalizeItem(
          {
            ...item,
            menuItemId: Number(`${scenario.scenarioId}${index + 1}`),
          },
          scenario.scenarioId,
          index,
        ),
      );
      this.selectedByScenario[scenario.scenarioId] = new Set<string>();
    });

    this.activeScenarioId = this.scenarioTabs[0]?.scenarioId ?? null;
  }

  private toDaysheetMenuItemPayload(item: MenuDialogItem): DaysheetMenuItemPayload {
    const status = String(this.itemStatusById[item.id] ?? '1');
    return {
      daysheetMenuItemName: item.daysheetMenuItemName || item.itemName,
      daysheetMenuItemId: item.menuItemId,
      status,
      itemStatus: status,
    };
  }

  private resetItemStatusTracking(): void {
    this.itemStatusById = {};
    this.savedItemIds = new Set<string>();
  }

  private normalizeItem(
    item: Partial<MenuDialogItem> & { id: string; itemName: string },
    scenarioId: number,
    index: number,
  ): MenuDialogItem {
    const itemName = String(item.itemName ?? '').trim();
    const daysheetMenuItemName = String(item.daysheetMenuItemName ?? itemName).trim();
    return {
      id: item.id || `scenario-${scenarioId}-${item.menuItemId ?? index}`,
      menuItemId: Number(item.menuItemId ?? 0),
      itemName,
      daysheetMenuItemName,
      teluguItemName: String(item.teluguItemName ?? '').trim(),
      itemFusionId: item.itemFusionId,
    };
  }

  private getScenarioIcon(scenarioName: string): string {
    const key = scenarioName.toLowerCase();
    if (key.includes('breakfast')) {
      return 'free_breakfast';
    }
    if (key.includes('lunch')) {
      return 'lunch_dining';
    }
    if (key.includes('snack')) {
      return 'bakery_dining';
    }
    if (key.includes('dinner')) {
      return 'dinner_dining';
    }
    return 'restaurant';
  }

}
